import { Component, ViewChild, ElementRef, Input, AfterViewInit, OnInit, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take, map } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';
import { LoggerService } from '@app/core/infrastructure';
import { AudioFacade } from '@app/store/audio';
import { AvatarFacade } from '@app/store/avatar';
import { Avatar } from '@app/core/api/avatar';
import { CommonModule } from '@angular/common';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { SubtitleComponent } from '@app/components/subtitle/subtitle.component';
import { LoadingContainerComponent } from '@app/components/loading-container/loading-container.component';
import { LowMinutesToastComponent } from '@app/components/low-minutes-toast/low-minutes-toast.component';
import { AvatarBoxFullState } from '@app/store/audio/models/audio-state.models';
import { FeedbackInfrastructureService } from '@app/core/infrastructure';
import { SharedModule } from '@app/shared/modules/shared.module';
import { environment } from '@environment/environment';

@UntilDestroy()
@Component({
  selector: 'app-avatar-box',
  templateUrl: './avatar-box.component.html',
  styleUrls: ['./avatar-box.component.scss'],
  standalone: true,
  imports: [CommonModule, MaterialSharedModule, SubtitleComponent, LoadingContainerComponent, LowMinutesToastComponent, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AvatarBoxComponent implements OnInit, AfterViewInit {
  private readonly logger = inject(LoggerService).scope('AvatarBoxComponent');
  
  @Input() showDottedFace = true;

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('backgroundVideo') backgroundVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('audioElement') audioElement!: ElementRef<HTMLAudioElement>;
  @ViewChild('localCameraPreview') localCameraPreview!: ElementRef<HTMLVideoElement>;

  // State from NgRx store via facade
  avatar$: Observable<Avatar | null>;
  isStreaming$: Observable<boolean>;
  isMinimizedMode$: Observable<boolean>;
  isSessionActive$: Observable<boolean>;
  isSessionInactive$: Observable<boolean>;
  conversationState$!: Observable<AvatarBoxFullState>;
  subtitlesEnabled$!: Observable<boolean>;
  interpreterMode$: Observable<any>;

  // Video loading state
  isBackgroundVideoLoading = signal(true);
  isStreamingVideoLoading = signal(true);

  // Feature flags
  readonly interpreterModeEnabled = environment.features?.interpreterMode ?? false;

  constructor(
    public audioFacade: AudioFacade,
    private avatarFacade: AvatarFacade,
    private router: Router,
    private feedbackInfrastructure: FeedbackInfrastructureService
  ) {
    // Initialize observables from facades
    this.avatar$ = this.avatarFacade.activeAvatar$;
    this.isStreaming$ = this.audioFacade.isStreaming$;
    this.isMinimizedMode$ = this.audioFacade.avatarBoxMode$.pipe(
      map(mode => mode === 'minimized')
    );
    this.conversationState$ = this.audioFacade.avatarBoxFullState$;
    this.isSessionActive$ = this.conversationState$.pipe(
      map(state => state.isStreaming && !state.isLoading && !state.isStoppingCall)
    );
    this.isSessionInactive$ = this.conversationState$.pipe(
      map(state => !state.isStreaming || state.isLoading || state.isStoppingCall)
    );
    this.interpreterMode$ = this.audioFacade.interpreterMode$;
  }

  ngOnInit() {
    // Subscribe to state changes for logging
  
    this.avatar$
      .pipe(untilDestroyed(this))
      .subscribe((avatar: Avatar | null) => {
        this.logger.debug('Avatar changed:', avatar?.name || 'none');
        // Reset video loading state when avatar changes
        if (avatar) {
          this.isBackgroundVideoLoading.set(true);
        }
      });
    
    this.isStreaming$
      .pipe(untilDestroyed(this))
      .subscribe((streaming: boolean) => {
        this.logger.debug('Streaming state changed:', streaming);
      });

    // Initialize subtitles enabled state from facade
    this.subtitlesEnabled$ = this.audioFacade.isSubtitleEnabled$;

    // Debug: Log interpreter mode changes
    this.interpreterMode$
      .pipe(untilDestroyed(this))
      .subscribe((mode) => {
        this.logger.debug('Interpreter mode state:', mode);
      });
  }

  async start() {
    // Use the new conversation control flow which handles video elements automatically
    this.audioFacade.startConversation();
  }

  stop() {
    this.audioFacade.stopConversation();
  }

  startVisualSession(): void {
    this.audioFacade.startVisualSession();
  }

  stopVisualSession(): void {
    this.audioFacade.stopVisualSession();
  }

  startUserCamera(): void {
    this.audioFacade.startCameraPreview();
  }

  stopUserCamera(): void {
    this.audioFacade.stopCameraPreview();
  }

  switchCamera(): void {
    this.audioFacade.switchCamera();
  }

  isCameraPreviewVisible(state: AvatarBoxFullState): boolean {
    if (state.isStoppingCall) {
      return false;
    }

    return state.cameraPreviewStatus === 'on' || state.isCameraSwitching;
  }

  isCameraPreviewLoading(state: AvatarBoxFullState): boolean {
    if (state.isStoppingCall) {
      return false;
    }

    return state.isCameraSwitching;
  }

  shouldShowSessionControls(state: AvatarBoxFullState): boolean {
    return state.isStreaming && !state.isLoading && !state.isStoppingCall;
  }

  toggleMicrophoneMute(): void {
    this.audioFacade.toggleMicrophoneMute();
  }

  onToggleSubtitles(): void {
    this.audioFacade.toggleSubtitles();
  }

  onExpand() {
    this.router.navigate(['/home']);
  }

  ngAfterViewInit() {
    // Configure video elements to prevent mobile fullscreen and controls
    if (this.videoElement?.nativeElement) {
      const video = this.videoElement.nativeElement;
      video.controls = false;
      video.playsInline = true;
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('playsinline', '');
      video.disablePictureInPicture = true;
    }

    if (this.localCameraPreview?.nativeElement) {
      const cameraPreview = this.localCameraPreview.nativeElement;
      cameraPreview.controls = false;
      cameraPreview.muted = true;
      cameraPreview.autoplay = true;
      cameraPreview.playsInline = true;
      cameraPreview.setAttribute('webkit-playsinline', '');
      cameraPreview.setAttribute('playsinline', '');
      cameraPreview.disablePictureInPicture = true;
    }

    // Configure background video as well
    if (this.backgroundVideo?.nativeElement) {
      const bgVideo = this.backgroundVideo.nativeElement;
      bgVideo.controls = false;
      bgVideo.playsInline = true;
      bgVideo.setAttribute('webkit-playsinline', '');
      bgVideo.setAttribute('playsinline', '');
      bgVideo.disablePictureInPicture = true;
      
      // Fix race condition: check if video is already loaded from browser cache
      // canplay may have fired before Angular bound the event listener
      if (bgVideo.readyState >= 3 && this.isBackgroundVideoLoading()) {
        this.isBackgroundVideoLoading.set(false);
        this.logger.debug('Background video already cached');
      }
    }

    // Video elements are automatically detected by the audio system when starting a conversation
    // No need to manually update them since the component is never destroyed during navigation
  }

  shouldShowBackground(avatar: Avatar | null): boolean {
      // Show background on home page even without avatar (dotted face)
      const isHome = this.router.url === '/home';
      if (isHome) {
          return true;
      }
      // Show background if there's an avatar present
      return !!avatar;
  }

  navigateToAvatars(event?: Event) {
    // Stop event propagation to prevent parent click handlers
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.router.navigate(['/avatars']);
  }

  onBackgroundVideoLoaded(): void {
    // Add a small delay to ensure video is actually ready to display
    timer(50).pipe(take(1), untilDestroyed(this)).subscribe(() => {
      this.isBackgroundVideoLoading.set(false);
      this.logger.debug('Background video loaded');
    });
  }

  onBackgroundVideoError(event: Event): void {
    this.isBackgroundVideoLoading.set(false);
    this.logger.error('Background video failed to load:', event);
  }

  onStreamingVideoLoaded(): void {
    timer(50).pipe(take(1), untilDestroyed(this)).subscribe(() => {
      this.isStreamingVideoLoading.set(false);
      this.logger.debug('Streaming video loaded');
    });
  }

  onStreamingVideoError(event: Event): void {
    this.isStreamingVideoLoading.set(false);
    this.logger.error('Streaming video failed to load:', event);
  }

  openFeedbackDialog(): void {
    this.feedbackInfrastructure.showFeedbackDialogManually()
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  /**
   * Convert language code to uppercase for display
   * @param languageCode Lowercase language code from server (e.g., "en", "de", "fr")
   * @returns Uppercase language code (e.g., "EN", "DE", "FR")
   */
  getLanguageCode(languageCode: string | null): string {
    if (!languageCode) return '??';
    return languageCode.toUpperCase();
  }

  stopInterpreterMode() {
    this.logger.debug('User requested to end translation mode.');
    this.audioFacade.stopInterpreterMode();
  }

  // Remove ngOnDestroy - @UntilDestroy handles it
}